import { API_ENDPOINTS } from "../constants/api";
import { RESTRICTED } from "../constants/storage";
import { summaryDefaults } from "../data/defaults";
import { sendLogToLoki } from "../utils/UtilsLoki";
import {
	handleAPIErrorBasedOnRcode,
	RCODE_ERROR_PAGES,
} from "../utils/UtilsRcode";
import { store } from "../store";
import {
	actionSetAuthenticated,
	actionLogout,
} from "../store/actions/user-actions";
import { AuthStatus } from "../types/user";

/**
 * Handles 401 responses by dispatching logout actions to Redux.
 * This ensures the frontend session state is cleared when the backend
 * session (HttpOnly cookie) has expired.
 */
const handle401 = () => {
	store.dispatch(actionSetAuthenticated(AuthStatus.Unauthenticated));
	store.dispatch(actionLogout());
};

const Http = {
	getHeaders: async (extraHeaders?: any) => {
		const defaultHeaders = {
			Pragma: "no-cache",
			"Cache-Control": "no-cache",
			"x-sc-useragent": ";;WebPortal=0.1;Windows NT 6.1; WOW64;",
			"Content-type": "application/json;charset=UTF-8",
			"x-sc-client-os-version": 27,
		};

		return extraHeaders
			? { ...defaultHeaders, ...extraHeaders }
			: defaultHeaders;
	},

	/**
	 * Checks if the API call should be aborted based on user restrictions and URL patterns
	 * @param url The API endpoint URL
	 * @returns true if the call should be aborted, false otherwise
	 */
	shouldAbortApiCall: (url: string): boolean => {
		try {
			// Check if user is restricted
			const isUserRestricted = sessionStorage.getItem(RESTRICTED) === "true";

			if (!isUserRestricted) {
				return false;
			}

			// Check if URL contains restricted paths
			const restrictedPaths = ["/summary", "/service"];
			const hasRestrictedPath = restrictedPaths.some((path) =>
				url.includes(path)
			);

			return hasRestrictedPath;
		} catch (error) {
			console.error("Error checking API call restrictions:", error);
			// If there's an error checking restrictions, allow the call to proceed
			return false;
		}
	},

	get: async (url: string, extraHeaders?: any, isPublic?: false) => {
		// Check if API call should be aborted due to user restrictions
		if (Http.shouldAbortApiCall(url)) {
			// Return empty response as mentioned in the existing comment
			if (url.includes(API_ENDPOINTS.SUMMARY)) return summaryDefaults;
			return {};
		}

		const headers = await Http.getHeaders(extraHeaders);
		// add debug for loki logs
		const response = await fetch(url, {
			method: "GET",
			headers: { ...headers },
			credentials: "include",
		});

		// 401 interceptor: session expired, clear auth state
		if (response.status === 401) {
			handle401();
		}

		const responseData = await response.json();

		if (RCODE_ERROR_PAGES.includes(responseData.rcode)) {
			sendLogToLoki(responseData.rmsg, responseData.rcode, "error", url);
			handleAPIErrorBasedOnRcode(responseData.rcode);
		}
		if (!response.ok) {
			response.text().then((errorMessage) => {
				sendLogToLoki(errorMessage, response.status, "error", url);
			});
		}

		return responseData;
	},

	post: async (
		url: string,
		data?: any,
		extraHeaders?: any,
		controller?: any
	) => {
		try {
			if (Http.shouldAbortApiCall(url)) {
				// Return empty response as mentioned in the existing comment
				return {};
			}

			const headers = await Http.getHeaders(extraHeaders);
			// add debug for loki logs
			const response = await fetch(url, {
				method: "POST",
				headers,
				body: JSON.stringify(data),
				credentials: "include",
				...controller,
			});

			// 401 interceptor: session expired, clear auth state
			if (response.status === 401) {
				handle401();
			}

			const responseData = await response.json();

			// handle http errors
			if (!response.ok && !responseData.rcode) {
				const errorText = await response.text(); // await to read the response text
				throw new Error(`HTTP: ${response.status} - ${errorText}`);
			}

			// handle http errors
			if (!response.ok && !responseData.rcode) {
				const errorText = await response.text(); // await to read the response text
				sendLogToLoki(errorText, response.status, "error", url);
				throw new Error(`HTTP: ${response.status} - ${errorText}`);
			}

			// handle rcodes error
			if (responseData.rcode > 0) {
				sendLogToLoki(responseData.rmsg, responseData.rcode, "error", url);
				handleAPIErrorBasedOnRcode(responseData.rcode);
				return Promise.reject(responseData);
			}

			return responseData;
		} catch (e: any) {
			console.error(e);
			throw e;
		}
	},
};

export default Http;
